import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { type PageLink } from '@/_metronic/layout/core';
import { ANNOUNCEMENTS_PAGE_URL } from '@/api/core/links';
import { AnnouncementEndpoints } from '@/api/endpoints/AnnouncementEndpoints';
import MetronicCard from '@/app/components/layout/MetronicCard';
import PageShell from '@/app/components/layout/PageShell';
import { type DataTableColumnDef } from '@/app/models/DataTableColumnDef';
import { DataTable } from '@/app/modules/table/DataTable';
import ReceiptStatusFilter from '@/app/pages/notifications/announcements/components/ReceiptStatusFilter';
import { receiptReportColumns } from '@/app/pages/notifications/announcements/core/_columns';
import { useAnnouncementDetails } from '@/app/pages/notifications/announcements/core/_hooks';

const REPORT_TITLE = 'Relatório de ciência';

const ANNOUNCEMENT_RECEIPT_REPORT_TABLE_KEY = 'announcement-receipt-report';

const breadcrumbs: PageLink[] = [
  { title: 'Comunicados', path: ANNOUNCEMENTS_PAGE_URL, isSeparator: false, isActive: false },
  { title: '', path: '', isSeparator: true, isActive: false },
];

const AnnouncementReportPage = () => {
  const { id } = useParams();
  const { data: announcement, isPending, isError } = useAnnouncementDetails(id, true, true);
  const announcementType = announcement?.type.value;
  const columns = useMemo(() => {
    const reportColumns = receiptReportColumns(announcementType);
    return reportColumns as DataTableColumnDef<object>[];
  }, [announcementType]);

  if (isPending) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title={REPORT_TITLE}>
        <output className="text-gray-600 py-10 text-center d-block">Carregando comunicado...</output>
      </PageShell>
    );
  }

  if (isError) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title={REPORT_TITLE}>
        <div className="alert alert-danger">Não foi possível carregar o comunicado.</div>
      </PageShell>
    );
  }

  const title = `${REPORT_TITLE}: ${announcement.title}`;
  const receiptsEndpoint = AnnouncementEndpoints.receipts(announcement.id);

  return (
    <PageShell breadcrumbs={breadcrumbs} title={title}>
      <MetronicCard className="card-flush shadow-none border">
        <div className="card-body">
          <DataTable
            columns={columns}
            direction="asc"
            endpoint={receiptsEndpoint}
            filterComponents={[<ReceiptStatusFilter key="receipt-status-filter" />]}
            isRefreshable={true}
            searchPlaceHolder="Pesquise pelo nome"
            sort="user_name"
            tableKey={ANNOUNCEMENT_RECEIPT_REPORT_TABLE_KEY}
            variant="report"
          />
        </div>
      </MetronicCard>
    </PageShell>
  );
};

export default AnnouncementReportPage;
